/**
 * Admin waitlist API — surfaces the public waitlist's pending +
 * recently-invited queues, plus the mark-invited action.
 *
 *   GET   /admin/waitlist                 — pending + recentlyInvited + counts
 *   POST  /admin/waitlist/:id/mark-invited — flips invited_at = now
 *
 * Backed by moderation-service (it already has makeAdminGuard wired);
 * the underlying table is `waitlist_signups` from @funkihoo/db.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.js';

export interface WaitlistPendingRow {
  id: string;
  email: string;
  ipCountry: string | null;
  createdAt: string;
}

export interface WaitlistInvitedRow extends WaitlistPendingRow {
  invitedAt: string;
  joinedUserId: string | null;
}

export interface WaitlistResponse {
  pending: WaitlistPendingRow[];
  recentlyInvited: WaitlistInvitedRow[];
  counts: {
    pending: number;
    invited: number;
    joined: number;
  };
}

export const waitlistKeys = {
  all: ['admin-waitlist'] as const,
  list: () => [...waitlistKeys.all, 'list'] as const,
} as const;

export function useWaitlistQuery() {
  return useQuery({
    queryKey: waitlistKeys.list(),
    queryFn: () =>
      apiFetch<WaitlistResponse>({
        method: 'GET',
        url: '/admin/waitlist',
      }),
  });
}

export interface MarkInvitedResponse {
  id: string;
  invitedAt: string;
  alreadyInvited: boolean;
}

export function useMarkInvitedMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<MarkInvitedResponse>({
        method: 'POST',
        url: `/admin/waitlist/${encodeURIComponent(id)}/mark-invited`,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: waitlistKeys.list() });
    },
  });
}
